import { useState } from 'react';
import { createPortal } from 'react-dom';
import NivaLogo from './NivaLogo';
import { apiPost } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const TERMS_URL = 'https://www.sawtna.app/terms-and-conditions';
const PRIVACY_URL = 'https://www.sawtna.app/privacy-policy';

export default function ConsentModal() {
  const { t, isRtl } = useLanguage();
  const { markConsentAccepted } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [voiceConsent, setVoiceConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!termsAccepted || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      await apiPost('/api/nurse/consent', {
        terms_accepted: true,
        voice_improvement_consent: voiceConsent,
      });
      markConsentAccepted();
    } catch (err) {
      setError(err.message || t('consentSubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-title"
        aria-describedby="consent-subtitle"
        dir={isRtl ? 'rtl' : 'ltr'}
        className="flex max-h-[min(92vh,720px)] w-full max-w-md flex-col overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <NivaLogo className="mb-6" />
        <h1 id="consent-title" className="text-center text-2xl font-bold text-slate-900">
          {t('consentTitle')}
        </h1>
        <p id="consent-subtitle" className="mt-2 text-center text-sm text-gray-500">
          {t('consentSubtitle')}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 flex flex-1 flex-col gap-5">
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-700">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
              required
            />
            <span>
              {t('consentTermsPrefix')}{' '}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline underline-offset-2"
                onClick={(e) => e.stopPropagation()}
              >
                {t('consentTermsOfService')}
              </a>{' '}
              {t('consentAnd')}{' '}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline underline-offset-2"
                onClick={(e) => e.stopPropagation()}
              >
                {t('consentPrivacyPolicy')}
              </a>
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-700">
            <input
              type="checkbox"
              checked={voiceConsent}
              onChange={(e) => setVoiceConsent(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span>{t('consentVoiceLabel')}</span>
          </label>

          {error ? (
            <div className="rounded-xl bg-critical-light px-4 py-3 text-sm text-critical-dark">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!termsAccepted || submitting}
            className={`mt-auto w-full rounded-xl py-3.5 text-base font-semibold transition ${
              termsAccepted && !submitting
                ? 'bg-primary text-white hover:opacity-95'
                : 'cursor-not-allowed bg-gray-200 text-gray-400'
            }`}
          >
            {submitting ? t('consentSubmitting') : t('consentGetStarted')}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
